//
// Copyright © 2026 Diloxy.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

// Copying shown text lets the browser write the theme's colours, backgrounds and fonts into the
// clipboard, so pasting elsewhere brings Huly's look along. This writes the selection's own markup
// instead: bold, links, lists, tables and pictures stay, and the pasted text takes the look of
// where it lands.

const inlineFormattingTags = new Set(['A', 'B', 'STRONG', 'I', 'EM', 'U', 'S', 'DEL', 'CODE', 'MARK', 'SUB', 'SUP'])
const listTags = new Set(['UL', 'OL'])
const tableTags = new Set(['TABLE', 'THEAD', 'TBODY', 'TFOOT', 'TR'])

// Icons and media have no size without the page's styles, and controls make no sense outside Huly.
const droppedElements = [
  'svg',
  'button',
  'script',
  'style',
  'noscript',
  'template',
  'input',
  'textarea',
  'select',
  'video',
  'audio',
  'canvas',
  'iframe'
].join(', ')

export function handleCleanCopy (event: ClipboardEvent): void {
  // Editors such as ProseMirror write their own clipboard data and mark the event handled.
  if (event.defaultPrevented || event.clipboardData == null || isTextField(event.target)) return

  const selection = document.getSelection()
  if (selection == null || selection.isCollapsed || selection.rangeCount === 0) return

  const html = getCleanHtml(selection, isHiddenOnScreen)
  if (html === '') return

  event.clipboardData.setData('text/html', html)
  event.clipboardData.setData('text/plain', selection.toString())
  event.preventDefault()
}

export function getCleanHtml (selection: Selection, isHidden: (element: Element) => boolean): string {
  const container = document.createElement('div')

  for (let index = 0; index < selection.rangeCount; index++) {
    container.append(cloneRange(selection.getRangeAt(index), isHidden))
  }

  container.querySelectorAll(droppedElements).forEach((element) => {
    element.remove()
  })
  container.querySelectorAll('[style]').forEach((element) => {
    element.removeAttribute('style')
  })

  return container.innerHTML.trim()
}

function isTextField (target: EventTarget | null): boolean {
  return target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement
}

// Hidden parts, such as a follow-up message's time, would show once the page's styles are gone.
function isHiddenOnScreen (element: Element): boolean {
  const style = window.getComputedStyle(element)
  return style.getPropertyValue('visibility') === 'hidden' || style.getPropertyValue('display') === 'none'
}

function cloneRange (range: Range, isHidden: (element: Element) => boolean): Node {
  const fragment = range.cloneContents()
  const copies = Array.from(fragment.querySelectorAll('*'))
  const originals = getSelectedElements(range)

  // Both lists follow document order, so each copy sits at its original's index.
  if (copies.length === originals.length) {
    copies.forEach((copy, index) => {
      takeScreenState(copy, originals[index], isHidden)
    })
  }

  return withInlineFormatting(range, withStructure(range, fragment))
}

function getSelectedElements (range: Range): Element[] {
  const root = range.commonAncestorContainer
  if (!(root instanceof Element)) return []
  return Array.from(root.querySelectorAll('*')).filter((element) => range.intersectsNode(element))
}

// A copy keeps what its original shows on screen: hidden parts go, and pictures and links get
// full addresses so they still work in another program.
function takeScreenState (copy: Element, original: Element, isHidden: (element: Element) => boolean): void {
  if (isHidden(original)) {
    copy.remove()
    return
  }

  if (original instanceof HTMLImageElement) {
    copy.setAttribute('src', original.currentSrc !== '' ? original.currentSrc : original.src)
    copy.removeAttribute('srcset')

    // Without the page's styles a picture without its own size pastes at full size.
    if (!copy.hasAttribute('width') && !copy.hasAttribute('height') && original.width > 0 && original.height > 0) {
      copy.setAttribute('width', `${original.width}`)
      copy.setAttribute('height', `${original.height}`)
    }
  }

  if (original instanceof HTMLAnchorElement && original.href !== '') {
    copy.setAttribute('href', original.href)
  }
}

// Several list items or table cells keep their list or table around them.
function withStructure (range: Range, content: Node): Node {
  const root = range.commonAncestorContainer
  if (!(root instanceof HTMLElement)) return content
  if (listTags.has(root.tagName)) return wrap(root, content)
  if (!tableTags.has(root.tagName)) return content

  let result = content
  for (let node: HTMLElement | null = root; node !== null; node = node.parentElement) {
    result = wrap(node, result)
    if (node.tagName === 'TABLE') break
  }
  return result
}

// A phrase selected inside a link or bold text keeps that link or bold.
function withInlineFormatting (range: Range, content: Node): Node {
  let result = content

  for (
    let node: Node | null = range.commonAncestorContainer;
    node !== null && node !== document.body;
    node = node.parentNode
  ) {
    if (node instanceof HTMLElement && inlineFormattingTags.has(node.tagName)) {
      result = wrap(node, result)
    }
  }

  return result
}

function wrap (element: Node, content: Node): Node {
  const wrapper = element.cloneNode(false)
  if (wrapper instanceof HTMLAnchorElement && element instanceof HTMLAnchorElement && element.href !== '') {
    wrapper.setAttribute('href', element.href)
  }
  wrapper.appendChild(content)
  return wrapper
}
